import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { makeStyles } from "components/mui-core";
import { styled } from "@mui/system";
import { ClampedText } from "components-redesign/ClampedText/ClampedText";
import { FlexContent } from "components/FlexContent/FlexContent";
import { ReactComponent as ChangeHistoryIcon } from "icons/changeHistory/change-history.svg";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { getAllCampaigns } from "redux/campaigns/selectors";
import { getAllCreativesObject } from "redux/creatives/selectors";
import useCreativesDataProvider from "redux/creatives/useCreatives";
import { ChangeHistoryProvider } from "./ChangeHistoryContext";
import { ChangeHistoryTable } from "./ChangeHistoryTable";

const useStyles = makeStyles()({
  dialog: {
    "& .MuiDialog-container": {
      justifyContent: "flex-end",
    },
  },
  dialogPaper: {
    minWidth: "1092px",
    height: "calc(100vh - 32px)",
    margin: "16px",
    maxHeight: "none",
    borderRadius: "20px",
  },
  dialogTitle: {
    backgroundColor: "#6750A4",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px 12px 26px",
  },
  closeButton: {
    color: "#fff",
    flexShrink: 0,
  },
  dialogContent: {
    padding: "0",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
});

const DialogTitleText = styled("div")({
  minWidth: 0,
});

type ChangeHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  campaignId?: string;
  lineitemId?: string;
  creativeId?: string;
  fieldPathPatterns?: string[];
};

export const ChangeHistoryDialog = ({
  open,
  onClose,
  campaignId: campaignIdProp,
  lineitemId: lineitemIdProp,
  creativeId,
  fieldPathPatterns,
}: ChangeHistoryDialogProps) => {
  const { classes } = useStyles();
  const location = useLocation();
  const campaigns = useSelector(getAllCampaigns);
  const creativesObject = useSelector(getAllCreativesObject);
  useCreativesDataProvider();

  // In case they want it back, setting this to true makes the Change History
  // check URL and automatically open for the URL we're in, for example if we're in campaign page:
  //
  // With it in false:
  // - Opening from sidebar icon will open Change History and show ALL changes org wide
  // - Opening from campaign actions dropdown will open Change history and be prefiltered to only show changes for that campaign
  //
  // With it in true:
  // - Always prefilter by that campaign
  const autoContextFromCurrentUrl = false;

  const getCampaignAndLineitemIdsFromUrl = () => {
    const pathname = location.pathname;

    const campaignMatch = pathname.match(/\/campaign\/([^/]+)/);
    const campaignId = campaignMatch ? campaignMatch[1] : undefined;

    const lineItemMatch = pathname.match(/\/lineItem\/([^/]+)/);
    const lineItemId = lineItemMatch ? lineItemMatch[1] : undefined;

    return { campaignId, lineItemId };
  };

  const urlIds = getCampaignAndLineitemIdsFromUrl();
  const campaignId =
    campaignIdProp ??
    (autoContextFromCurrentUrl ? urlIds.campaignId : undefined);
  const lineItemId =
    lineitemIdProp ??
    (autoContextFromCurrentUrl ? urlIds.lineItemId : undefined);

  const getTitle = () => {
    if (creativeId) {
      const creative = creativesObject[creativeId];
      if (creative) {
        return `Change History: ${creative.name}`;
      }
    }

    if (lineItemId) {
      for (const campaign of campaigns) {
        const lineitem = campaign.lineitems?.find((li) => li.id === lineItemId);
        if (lineitem) {
          return `Change History: ${lineitem.name}`;
        }
      }
    }

    if (campaignId) {
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (campaign) {
        return `Change History: ${campaign.name}`;
      }
    }

    return "Change History";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      classes={{
        paper: classes.dialogPaper,
      }}>
      <DialogTitle className={classes.dialogTitle}>
        <FlexContent
          align="center"
          gap="10px">
          <ChangeHistoryIcon />
          <ClampedText
            text={getTitle()}
            lines={3}
            component={DialogTitleText}
            placement="bottom"
          />
        </FlexContent>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <ChangeHistoryProvider
          campaignId={campaignId}
          lineitemId={lineItemId}
          creativeId={creativeId}
          fieldPathPatterns={fieldPathPatterns}>
          <ChangeHistoryTable />
        </ChangeHistoryProvider>
      </DialogContent>
    </Dialog>
  );
};
